import { useEffect, useState } from 'react'
import { MapContainer as LeafletMapContainer, TileLayer, useMap } from 'react-leaflet'
import { LatLngBounds } from 'leaflet'
import { Locale, MAPPA_CONFIG } from '../../types'
import LocaleMarker from './LocaleMarker'

interface MapComponentProps {
  locali: Locale[]
  selectedLocaleId?: number
  onLocaleClick?: (locale: Locale) => void
  height?: string
  className?: string
}

/**
 * Componente interno per gestire lo zoom sulla selezione
 */
function MapController({ selectedLocaleId, locali }: { selectedLocaleId?: number; locali: Locale[] }) {
  const map = useMap()

  useEffect(() => {
    if (selectedLocaleId) {
      const locale = locali.find(l => l.id === selectedLocaleId)
      if (locale) {
        map.setView([locale.latitudine, locale.longitudine], 14, { animate: true })
      }
    }
  }, [selectedLocaleId, locali, map])

  return null
}

/**
 * Componente per adattare la vista a tutti i marker
 */
function FitBounds({ locali }: { locali: Locale[] }) {
  const map = useMap()

  useEffect(() => {
    if (locali.length > 0) {
      const bounds = new LatLngBounds(
        locali.map(l => [l.latitudine, l.longitudine])
      )
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 })
    }
  }, [locali, map])

  return null
}

/**
 * Wrapper principale per la mappa Leaflet
 * Mostra i locali approvati con marker interattivi
 */
export default function MapComponent({
  locali,
  selectedLocaleId,
  onLocaleClick,
  height = '500px',
  className = '',
}: MapComponentProps) {
  const [mapReady, setMapReady] = useState(false)

  return (
    <div className={`relative rounded-xl overflow-hidden ${className}`} style={{ height }}>
      <LeafletMapContainer
        center={[MAPPA_CONFIG.centro.lat, MAPPA_CONFIG.centro.lng]}
        zoom={MAPPA_CONFIG.zoomIniziale}
        minZoom={MAPPA_CONFIG.zoomMin}
        maxZoom={MAPPA_CONFIG.zoomMax}
        className="w-full h-full z-0"
        whenReady={() => setMapReady(true)}
      >
        {/* Tile layer OpenStreetMap */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Controller per la selezione */}
        {mapReady && (
          <>
            <MapController selectedLocaleId={selectedLocaleId} locali={locali} />
            {locali.length > 1 && !selectedLocaleId && <FitBounds locali={locali} />}
          </>
        )}

        {/* Marker per ogni locale */}
        {locali.map((locale) => (
          <LocaleMarker
            key={locale.id}
            locale={locale}
            isSelected={locale.id === selectedLocaleId}
            onClick={() => onLocaleClick?.(locale)}
          />
        ))}
      </LeafletMapContainer>

      {/* Overlay con info */}
      <div className="absolute bottom-4 left-4 z-[1000] bg-night/80 backdrop-blur-sm px-3 py-1.5 rounded-lg">
        <p className="text-xs text-gray-400">
          <span className="text-gold font-semibold">{locali.length}</span>{' '}
          {locali.length === 1 ? 'locale approvato' : 'locali approvati'}
        </p>
      </div>
    </div>
  )
}
